import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  size?: ModalSize;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  footer?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Extra classes for the footer row (e.g. page-scoped Design System styling). */
  footerClassName?: string;
  headerVariant?: 'default' | 'tinted';
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  footer,
  className = '',
  bodyClassName = 'p-4',
  footerClassName = '',
  headerVariant = 'default',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Use a ref for onClose so the keyboard listener never goes stale
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Remember the opener during render: children (with `autoFocus`) commit
  // before any effect here runs, so by then activeElement is already inside.
  const wasOpen = useRef(false);
  if (isOpen && !wasOpen.current) {
    previousActiveElement.current = document.activeElement as HTMLElement;
  }
  wasOpen.current = isOpen;

  // Focus only when the modal first opens (not on every re-render)
  useEffect(() => {
    if (isOpen) {
      // Children mount (and apply `autoFocus`) before this effect runs — only
      // fall back to focusing the dialog surface when nothing inside it
      // already claimed focus, otherwise we'd steal it from e.g. an input.
      if (!modalRef.current?.contains(document.activeElement)) {
        modalRef.current?.focus();
      }
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    }
  }, [isOpen]);

  // Keyboard listener — stable, reads onClose via ref
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscape]);

  // Handle overlay click
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[1050] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 transition-opacity animate-fadeIn"
        aria-hidden="true"
        onClick={handleOverlayClick}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        tabIndex={-1}
        className={`
          au-modal-surface
          relative w-full ${sizeClasses[size]}
          transform transition-all animate-scaleIn
          ${className}
        `}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div
            className={`flex items-center justify-between p-4 au-modal-header${
              headerVariant === 'tinted'
                ? ' au-modal-header--tinted'
                : ' border-b border-au-divider'
            }`}
          >
            {title && (
              <h2 id="modal-title" className="text-lg font-semibold au-modal-title">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="p-1 rounded-au-base au-modal-close hover:bg-au-surface-sunken transition-colors"
                aria-label="Close modal"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={bodyClassName}>{children}</div>

        {/* Footer */}
        {footer && (
          <div
            className={`flex items-center justify-end gap-3 p-4${
              headerVariant === 'tinted' ? '' : ' border-t border-au-divider'
            }${footerClassName ? ` ${footerClassName}` : ''}`}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
